import React, { useState } from 'react'
import {
  MapPin, TrendingUp, ChevronDown, ChevronUp, Calculator,
  Lightbulb, ArrowRight, ArrowLeftRight, CheckCircle, XCircle,
  Briefcase, Star, Trophy, ChevronRight, Zap, Globe
} from 'lucide-react'

// ── 직업 스토리 시나리오 데이터 ───────────────────────────────────────────────
const SCENARIOS = [
  {
    id: 'programmer',
    emoji: '💻',
    title: '게임 개발자',
    category: 'IT',
    color: 'from-indigo-400 to-blue-500',
    bg: 'bg-indigo-50',
    border: 'border-indigo-200',
    badge: 'bg-indigo-100 text-indigo-700',
    story: `게임 개발자 지민이는 점수 시스템을 설계합니다.
금화 1개는 10점, 은화 1개는 5점을 줍니다.
매판 아이템 개수가 달라지기 때문에 공식이 필요해요.
"문자식만 있으면 어떤 경우도 한 번에 계산할 수 있어!"`,
    situation: '게임 점수: 금화 a개 × 10점 + 은화 b개 × 5점. 은화를 3개로 고정하면 y = 10a + 15',
    question: 'a = 4(금화 4개)일 때 총 점수는? 그리고 100점을 만들려면 금화 몇 개?',
    xLabel: '금화 개수(a)',
    yLabel: '총 점수(점)',
    equationLabel: 'y = 10a + 15',
    slopeVal: 10,
    slopeDesc: '금화 1개당 +10점 (변화율)',
    interceptVal: 15,
    interceptDesc: '은화 3개 × 5점 = 15점 (고정값)',
    answer: 'a=4 → y = 10×4 + 15 = 55점\n100점 → 10a + 15 = 100 → a = 8.5 → 금화 9개 이상',
    tableData: [
      { x: 0, y: 15 }, { x: 1, y: 25 }, { x: 2, y: 35 },
      { x: 3, y: 45 }, { x: 4, y: 55 }, { x: 5, y: 65 },
    ],
    whyMath: '게임 개발자는 점수·보상 공식을 문자식으로 설계합니다. 코딩의 변수(variable)는 수학의 문자와 완전히 같은 원리!',
    insight: '게임 개발자, 앱 개발자, AI 엔지니어는 매일 변수(문자)를 다룹니다. 수학의 문자식 = 코딩의 변수!',
    quiz: [
      { q: 'a=6, 은화 3개일 때 총 점수는?', a: '10×6+15 = 75점', hint: 'y = 10a + 15에 a=6 대입' },
      { q: '점수를 50점 만들려면 금화 몇 개? (은화 3개 고정)', a: '10a+15=50 → a=3.5 → 4개', hint: '10a = 50-15 = 35' },
    ],
    jobs: ['게임 개발자', '앱 개발자', 'AI 엔지니어', '데이터 과학자'],
  },
  {
    id: 'chef',
    emoji: '👨‍🍳',
    title: '셰프',
    category: '요리',
    color: 'from-orange-400 to-amber-500',
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    badge: 'bg-orange-100 text-orange-700',
    story: `서울 유명 레스토랑의 셰프 소연이는 쿠키를 대량 제작합니다.
원래 레시피(1인분): 밀가루 2컵, 설탕 1컵, 버터 0.5컵.
인원수에 따라 n배로 늘려야 합니다.
"n이라는 문자 하나로 어떤 배수도 즉시 계산!"`,
    situation: 'n배 레시피: 밀가루 2n컵 + 설탕 n컵 = 3n컵 (밀가루+설탕 합계)',
    question: 'n = 6(6배)일 때 밀가루+설탕 합계는? 그리고 합계가 15컵이 되는 n은?',
    xLabel: '배수(n)',
    yLabel: '밀가루+설탕(컵)',
    equationLabel: 'y = 3n',
    slopeVal: 3,
    slopeDesc: '배수 1당 +3컵 (밀가루 2 + 설탕 1)',
    interceptVal: 0,
    interceptDesc: '0배 → 0컵 (원점 통과, 정비례)',
    answer: 'n=6 → y = 3×6 = 18컵\n합계 15컵 → 3n = 15 → n = 5',
    tableData: [
      { x: 0, y: 0 }, { x: 1, y: 3 }, { x: 2, y: 6 },
      { x: 3, y: 9 }, { x: 5, y: 15 }, { x: 6, y: 18 },
    ],
    whyMath: '셰프는 레시피 비율을 문자식으로 관리합니다. 인원수 n만 바꾸면 모든 재료량이 자동으로 계산!',
    insight: '대형 식당·베이커리·식품 공장은 문자식으로 레시피를 관리합니다. 비율을 유지하면서 양을 조절하는 핵심 원리!',
    quiz: [
      { q: 'n=8일 때 밀가루+설탕 합계는?', a: '3×8 = 24컵', hint: 'y = 3n에 n=8 대입' },
      { q: '합계가 21컵이 되는 n은?', a: '3n=21 → n=7', hint: '3n = 21에서 n을 구하세요' },
    ],
    jobs: ['셰프', '제과·제빵사', '식품 공학자', '영양사'],
  },
  {
    id: 'designer',
    emoji: '🎨',
    title: '패션 디자이너',
    category: '디자인',
    color: 'from-pink-400 to-rose-500',
    bg: 'bg-pink-50',
    border: 'border-pink-200',
    badge: 'bg-pink-100 text-pink-700',
    story: `패션 디자이너 민아는 드레스를 디자인합니다.
치마 길이 x cm에 따라 소매=(x-20)cm, 상의=(x÷2+5)cm.
체형마다 x만 바꾸면 모든 치수가 자동으로 계산됩니다.
"x 하나로 전체 설계를 관리해!"`,
    situation: '소매+상의 합 = (x-20) + (x÷2+5) = 1.5x - 15 (cm)',
    question: 'x = 90일 때 소매+상의 합은? 합이 100cm가 되는 x는?',
    xLabel: '치마 길이(cm)',
    yLabel: '소매+상의 합(cm)',
    equationLabel: 'y = 1.5x - 15',
    slopeVal: 1.5,
    slopeDesc: 'x 1cm 증가 → 합 1.5cm 증가 (변화율)',
    interceptVal: -15,
    interceptDesc: 'x=0일 때 -15cm (y절편)',
    answer: 'x=90 → 1.5×90 - 15 = 120cm\n합=100 → 1.5x - 15 = 100 → x ≈ 76.7cm',
    tableData: [
      { x: 40, y: 45 }, { x: 60, y: 75 }, { x: 70, y: 90 },
      { x: 80, y: 105 }, { x: 90, y: 120 }, { x: 100, y: 135 },
    ],
    whyMath: '디자이너는 문자식으로 치수 비율을 관리합니다. x 하나로 모든 부위 치수를 동시에 계산!',
    insight: '패션 디자이너, 건축가, CAD 설계자는 치수 비율을 문자식으로 관리합니다. 프로의 필수 도구!',
    quiz: [
      { q: 'x=80일 때 소매+상의 합은?', a: '1.5×80-15 = 105cm', hint: 'y = 1.5x - 15에 x=80 대입' },
      { q: '합이 90cm가 되는 x는?', a: '1.5x-15=90 → x=70cm', hint: '1.5x = 105에서 x를 구하세요' },
    ],
    jobs: ['패션 디자이너', '건축가', '인테리어 디자이너', 'CAD 설계자'],
  },
  {
    id: 'scientist',
    emoji: '🔬',
    title: '화학 연구원',
    category: '과학',
    color: 'from-emerald-400 to-teal-500',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    badge: 'bg-emerald-100 text-emerald-700',
    story: `화학 연구원 준혁이는 소금물 농도를 실험합니다.
소금 a g을 물 100g에 녹이면 농도(%) = a÷(a+100)×100.
소금이 적을 때는 농도 ≈ a%로 근사할 수 있어요.
"하나의 공식으로 모든 경우를 표현해!"`,
    situation: '소금 a g, 물 100g → 농도(%) = a÷(a+100)×100 (단순화: 농도 ≈ a%)',
    question: 'a = 5일 때 정확한 농도는? 농도 10%를 만들려면 소금 몇 g?',
    xLabel: '소금(g)',
    yLabel: '농도(%)',
    equationLabel: '농도(%) = a÷(a+100)×100',
    slopeVal: 1,
    slopeDesc: '소금 1g ↑ → 농도 약 1% ↑ (근사값)',
    interceptVal: 0,
    interceptDesc: '소금 0g → 농도 0% (원점)',
    answer: 'a=5 → 5÷105×100 ≈ 4.76%\n농도 10% → 10=a÷(a+100)×100 → a ≈ 11.1g',
    tableData: [
      { x: 0, y: 0 }, { x: 5, y: 4.76 }, { x: 10, y: 9.09 },
      { x: 15, y: 13.04 }, { x: 20, y: 16.67 }, { x: 25, y: 20 },
    ],
    whyMath: '과학의 모든 법칙이 문자식입니다. F=ma, V=IR, PV=k… 문자식 없이 과학 연구 불가!',
    insight: '화학자, 물리학자, 의약품 연구원은 매일 문자식 공식을 다룹니다. 자연의 법칙은 모두 문자식!',
    quiz: [
      { q: 'a=10, 물 90g일 때 농도는?', a: '10÷100×100 = 10%', hint: '도수분모 = 소금+물 = 100g' },
      { q: 'a=20, 물 80g일 때 농도는?', a: '20÷100×100 = 20%', hint: '소금+물 = 100g' },
    ],
    jobs: ['화학 연구원', '의약품 개발자', '환경 과학자', '식품 과학자'],
  },
  {
    id: 'seller',
    emoji: '🛒',
    title: '쇼핑몰 운영자',
    category: '경영',
    color: 'from-violet-400 to-purple-500',
    bg: 'bg-violet-50',
    border: 'border-violet-200',
    badge: 'bg-violet-100 text-violet-700',
    story: `온라인 쇼핑몰 운영자 수아는 할인가를 계산합니다.
정가 a원인 상품에 20% 할인: 판매가 = a - 0.2a = 0.8a.
"a만 알면 판매가가 바로 나와. 엑셀 공식도 이거야!"
정가가 달라도 공식 하나로 모든 상품을 처리!`,
    situation: '판매가 = 0.8a 원 (20% 할인, a: 정가)',
    question: 'a = 3500일 때 판매가는? 판매가가 4000원이 되는 정가는?',
    xLabel: '정가(원)',
    yLabel: '판매가(원)',
    equationLabel: 'y = 0.8a',
    slopeVal: 0.8,
    slopeDesc: '정가 1원 → 판매가 0.8원 (20% 할인)',
    interceptVal: 0,
    interceptDesc: '정가 0원 → 판매가 0원 (원점)',
    answer: 'a=3500 → 0.8×3500 = 2800원\n판매가 4000 → 0.8a = 4000 → a = 5000원',
    tableData: [
      { x: 1000, y: 800 }, { x: 2000, y: 1600 }, { x: 3000, y: 2400 },
      { x: 3500, y: 2800 }, { x: 5000, y: 4000 }, { x: 10000, y: 8000 },
    ],
    whyMath: '쇼핑몰·마트·편의점의 모든 할인 계산이 문자식! 엑셀·POS 시스템의 공식도 동일 원리.',
    insight: '유통업, 이커머스, 금융업의 가격 산정·할인·수수료 계산 모두 문자식이 기본!',
    quiz: [
      { q: '정가 6000원에 20% 할인 판매가는?', a: '0.8×6000 = 4800원', hint: 'y = 0.8a에 a=6000 대입' },
      { q: '판매가가 7200원이 되는 정가는?', a: '0.8a=7200 → a=9000원', hint: '0.8a = 7200에서 a를 구하세요' },
    ],
    jobs: ['쇼핑몰 운영자', '유통 MD', '마케터', '데이터 분석가'],
  },
  {
    id: 'architect',
    emoji: '🏗️',
    title: '건축가',
    category: '건축',
    color: 'from-teal-400 to-cyan-500',
    bg: 'bg-teal-50',
    border: 'border-teal-200',
    badge: 'bg-teal-100 text-teal-700',
    story: `건축가 재원이는 아파트 단지를 설계합니다.
각 층 높이가 h m일 때 n층 건물의 전체 높이 = nh m.
바닥 기초 두께 1.5m를 더하면 총 높이 = nh + 1.5.
"h와 n 두 문자로 어떤 건물도 설계할 수 있어!"`,
    situation: '총 높이 = nh + 1.5 (m). 층수 n=10, 층고 h=3m이면?',
    question: 'n=10, h=3일 때 총 높이는? 총 높이가 31.5m인 건물의 층고는? (10층 기준)',
    xLabel: '층고(h, m)',
    yLabel: '총 높이(m)',
    equationLabel: 'y = 10h + 1.5',
    slopeVal: 10,
    slopeDesc: '층고 1m 증가 → 총 높이 10m 증가 (10층)',
    interceptVal: 1.5,
    interceptDesc: '기초 두께 1.5m (y절편)',
    answer: 'n=10, h=3 → 10×3 + 1.5 = 31.5m\n총 높이 31.5m → 10h+1.5=31.5 → h=3m',
    tableData: [
      { x: 2, y: 21.5 }, { x: 2.5, y: 26.5 }, { x: 2.8, y: 29.5 },
      { x: 3, y: 31.5 }, { x: 3.5, y: 36.5 }, { x: 4, y: 41.5 },
    ],
    whyMath: '건축가는 문자식으로 건물 설계도를 작성합니다. 변수 하나만 바꿔도 전체 치수가 자동으로 조정됩니다!',
    insight: '건축가, 토목 엔지니어, 인테리어 디자이너는 문자식으로 면적·높이·재료량을 계산합니다.',
    quiz: [
      { q: 'n=15, h=2.8일 때 총 높이는?', a: '10×2.8+1.5=29.5m (n=10 기준) / 15×2.8+1.5=43.5m', hint: 'y = nh + 1.5에 n, h 대입' },
      { q: '10층 건물 총 높이를 28.5m로 하려면 층고는?', a: '10h+1.5=28.5 → h=2.7m', hint: '10h = 27' },
    ],
    jobs: ['건축가', '토목 엔지니어', '인테리어 디자이너', '도시 계획가'],
  },
  {
    id: 'doctor',
    emoji: '👨‍⚕️',
    title: '의사',
    category: '의료',
    color: 'from-sky-400 to-blue-500',
    bg: 'bg-sky-50',
    border: 'border-sky-200',
    badge: 'bg-sky-100 text-sky-700',
    story: `소아과 의사 서연이는 약 용량을 계산합니다.
체중 w kg인 아이에게 하루 용량 = 5w mg.
"체중(w)이라는 문자 하나로 모든 환자의 용량을 계산해!"
약을 너무 많거나 적게 줘선 안 되기 때문에 정확한 공식이 필요해요.`,
    situation: '약 하루 용량 = 5w mg (w: 체중 kg). 체중 20kg 아이의 용량은?',
    question: 'w = 20일 때 하루 용량은? 용량을 150mg으로 맞추려면 체중은?',
    xLabel: '체중(kg)',
    yLabel: '약 용량(mg)',
    equationLabel: 'y = 5w',
    slopeVal: 5,
    slopeDesc: '체중 1kg당 +5mg (용량 비율)',
    interceptVal: 0,
    interceptDesc: '체중 0kg → 0mg (원점, 정비례)',
    answer: 'w=20 → 5×20 = 100mg\n용량 150mg → 5w=150 → w=30kg',
    tableData: [
      { x: 10, y: 50 }, { x: 15, y: 75 }, { x: 20, y: 100 },
      { x: 25, y: 125 }, { x: 30, y: 150 }, { x: 40, y: 200 },
    ],
    whyMath: '의사는 약 용량·점적 속도·농도를 문자식으로 계산합니다. 환자 안전이 수학 공식에 달려있습니다!',
    insight: '의사, 약사, 간호사, 임상 연구원은 매일 용량·농도·체표면적 공식을 문자식으로 계산합니다.',
    quiz: [
      { q: 'w=15kg 아이의 하루 용량은?', a: '5×15 = 75mg', hint: 'y = 5w에 w=15 대입' },
      { q: '용량이 80mg이 되는 체중은?', a: '5w=80 → w=16kg', hint: '5w = 80에서 w를 구하세요' },
    ],
    jobs: ['소아과 의사', '약사', '임상 연구원', '의료 AI 개발자'],
  },
]

// ── 직업 탐색 데이터 ──────────────────────────────────────────────────────────
const CAREERS = [
  {
    icon: '💻',
    title: '소프트웨어 엔지니어',
    example: '점수 공식, 가격 계산 알고리즘을 문자식(변수)으로 설계',
    formula: 'score = 10 * gold + 5 * silver',
    salary: '연 5,000만~1억원',
    color: 'from-blue-500 to-indigo-500',
  },
  {
    icon: '📊',
    title: '데이터 분석가',
    example: '지표 계산식, 예측 모델을 문자식으로 표현',
    formula: 'revenue = price * qty - cost',
    salary: '연 4,000만~8,000만원',
    color: 'from-purple-500 to-pink-500',
  },
  {
    icon: '👨‍🍳',
    title: '셰프·식품 전문가',
    example: '레시피 비율·원가를 n배 문자식으로 관리',
    formula: 'total = unit_cost × n + fixed',
    salary: '연 3,500만~7,000만원',
    color: 'from-amber-500 to-orange-500',
  },
  {
    icon: '👨‍⚕️',
    title: '의사·의료 연구자',
    example: '약 용량, 농도 공식을 체중 문자식으로 계산',
    formula: 'dose = 5 × weight (mg)',
    salary: '연 8,000만~2억원',
    color: 'from-sky-500 to-blue-500',
  },
  {
    icon: '🏗️',
    title: '건축·인테리어 설계자',
    example: '면적·둘레·재료량을 문자식으로 산출',
    formula: 'area = length × width',
    salary: '연 4,500만~9,000만원',
    color: 'from-teal-500 to-emerald-500',
  },
  {
    icon: '🎨',
    title: '패션·제품 디자이너',
    example: '치수 비율, 패턴 설계를 x 하나로 관리',
    formula: 'sleeve = x - 20 (cm)',
    salary: '연 3,500만~7,500만원',
    color: 'from-pink-500 to-rose-500',
  },
]

// ── 도전 퀴즈 ─────────────────────────────────────────────────────────────────
const CHALLENGES = [
  {
    id: 'c1',
    emoji: '🎮',
    level: '⭐ 쉬움',
    levelColor: 'bg-emerald-100 text-emerald-700',
    question: '한 개에 x원인 음료수 5개를 사고 1000원짜리 봉투를 샀을 때 총 금액을 문자식으로 나타내고, x=800일 때 총 금액을 구하세요.',
    hint: '총 금액 = 음료수 5개 + 봉투 = 5x + 1000. x=800 대입!',
    answer: '5x + 1000, x=800이면 5000원',
    explanation: '5개 × x원 = 5x, 봉투 1000원 → 5x+1000\nx=800 → 5×800+1000 = 4000+1000 = 5000원!',
  },
  {
    id: 'c2',
    emoji: '🌿',
    level: '⭐⭐ 보통',
    levelColor: 'bg-amber-100 text-amber-700',
    question: '직사각형 텃밭의 가로 a m, 세로 (a-3) m일 때 둘레를 문자식으로 나타내고, a=8일 때 구하세요.',
    hint: '둘레 = 2×(가로+세로) = 2×(a + a-3) = 2(2a-3) = 4a-6',
    answer: '4a-6, a=8일 때 26m',
    explanation: '둘레 = 2(a + a-3) = 2(2a-3) = 4a-6\na=8 → 4×8-6 = 32-6 = 26m!',
  },
  {
    id: 'c3',
    emoji: '💰',
    level: '⭐⭐ 보통',
    levelColor: 'bg-amber-100 text-amber-700',
    question: '한 달 용돈 m원 중 간식에 m/4원, 저축에 m/2원을 씁니다. 나머지 용돈을 문자식으로 나타내고, m=40000일 때를 구하세요.',
    hint: '남은 용돈 = m - m/4 - m/2 = 4m/4 - m/4 - 2m/4 = m/4',
    answer: 'm/4원, m=40000일 때 10000원',
    explanation: 'm - m/4 - m/2 = m(1 - 1/4 - 1/2) = m/4\n동류항 계산의 핵심!\nm=40000 → 40000/4 = 10000원!',
  },
  {
    id: 'c4',
    emoji: '🏃',
    level: '⭐⭐⭐ 어려움',
    levelColor: 'bg-red-100 text-red-700',
    question: '시속 v km로 t시간 달린 거리의 2배보다 10km 짧은 거리를 문자식으로 나타내고, v=60, t=2일 때 구하세요.',
    hint: '달린 거리 = vt km. 그 2배보다 10km 짧으면 → 2vt - 10',
    answer: '2vt - 10, v=60·t=2이면 230km',
    explanation: '거리=vt, 2배=2vt, 10 짧으면=2vt-10\n문자식의 조합 활용!\nv=60, t=2 → 2×60×2-10 = 240-10 = 230km!',
  },
]

const CATEGORY_COLORS: Record<string, string> = {
  'IT':   'bg-indigo-100 text-indigo-700',
  '요리': 'bg-orange-100 text-orange-700',
  '디자인':'bg-pink-100 text-pink-700',
  '과학': 'bg-emerald-100 text-emerald-700',
  '경영': 'bg-violet-100 text-violet-700',
  '건축': 'bg-teal-100 text-teal-700',
  '의료': 'bg-sky-100 text-sky-700',
}

// ── 계산기 컴포넌트 ───────────────────────────────────────────────────────────
type CalcMode = 'findY' | 'findX'

function ScenarioCalculator({ scenario }: { scenario: typeof SCENARIOS[0] }) {
  const [xVal, setXVal] = useState('')
  const [yVal, setYVal] = useState('')
  const [mode, setMode] = useState<CalcMode>('findY')
  const [result, setResult] = useState<{ steps: string[]; outputVal: number } | null>(null)
  const [userAnswer, setUserAnswer] = useState('')
  const [checked, setChecked] = useState<boolean | null>(null)

  const slope = scenario.slopeVal
  const intercept = scenario.interceptVal

  const reset = () => { setXVal(''); setYVal(''); setResult(null); setUserAnswer(''); setChecked(null) }
  const handleModeChange = (m: CalcMode) => { setMode(m); reset() }

  const calculate = () => {
    if (mode === 'findY') {
      const x = parseFloat(xVal)
      if (isNaN(x)) return
      const y = Math.round((slope * x + intercept) * 100) / 100
      setResult({
        outputVal: y,
        steps: [
          `공식: ${scenario.equationLabel}`,
          `x = ${x} 대입`,
          `y = ${slope} × ${x} + ${intercept}`,
          `y = ${slope * x} + ${intercept} = ${y}`,
        ],
      })
    } else {
      const y = parseFloat(yVal)
      if (isNaN(y) || slope === 0) return
      const x = Math.round(((y - intercept) / slope) * 100) / 100
      setResult({
        outputVal: x,
        steps: [
          `공식: ${scenario.equationLabel}`,
          `y = ${y} 대입`,
          `${y} = ${slope}x + ${intercept}`,
          `${slope}x = ${y - intercept}`,
          `x = ${y - intercept} ÷ ${slope} = ${x}`,
        ],
      })
    }
    setUserAnswer('')
    setChecked(null)
  }

  const checkAnswer = () => {
    if (!result) return
    const ua = parseFloat(userAnswer.replace(/,/g, ''))
    const tolerance = Math.abs(result.outputVal) * 0.02 + 0.01
    setChecked(Math.abs(ua - result.outputVal) <= tolerance)
  }

  return (
    <div className="card border-2 border-violet-100">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-bold text-slate-700 flex items-center gap-2">
          <Calculator size={15} className="text-violet-600" />
          직접 계산해보기
        </p>
        <div className="flex rounded-lg overflow-hidden border border-slate-200 text-xs">
          <button
            onClick={() => handleModeChange('findY')}
            className={`px-3 py-1.5 font-medium transition-colors flex items-center gap-1 ${
              mode === 'findY' ? 'bg-violet-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <ArrowRight size={11} />
            x → y 구하기
          </button>
          <button
            onClick={() => handleModeChange('findX')}
            className={`px-3 py-1.5 font-medium transition-colors flex items-center gap-1 ${
              mode === 'findX' ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <ArrowLeftRight size={11} />
            y → x 구하기
          </button>
        </div>
      </div>

      <div className="mb-4 bg-slate-50 rounded-xl px-4 py-3 flex items-center gap-3">
        <span className="text-xs text-slate-400 font-medium">문자식</span>
        <span className="font-mono font-bold text-violet-700 text-sm">{scenario.equationLabel}</span>
      </div>

      <div className="flex gap-3 mb-4">
        <div className="flex-1">
          <label className="text-xs font-semibold text-slate-500">
            {mode === 'findY' ? `🔢 x 값 입력 (${scenario.xLabel})` : `🔢 y 값 입력 (${scenario.yLabel})`}
          </label>
          <input
            type="number"
            value={mode === 'findY' ? xVal : yVal}
            onChange={e => { mode === 'findY' ? setXVal(e.target.value) : setYVal(e.target.value); setResult(null); setChecked(null) }}
            onKeyDown={e => e.key === 'Enter' && calculate()}
            placeholder={mode === 'findY' ? 'x 값 입력' : 'y 값 입력'}
            className="w-full mt-1 text-base border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-300 transition-all"
          />
        </div>
        <div className="flex items-end">
          <button
            onClick={calculate}
            disabled={mode === 'findY' ? !xVal : !yVal}
            className="px-5 py-2.5 rounded-xl font-bold text-sm bg-violet-600 hover:bg-violet-700 text-white disabled:bg-slate-200 disabled:text-slate-400 transition-all"
          >
            계산!
          </button>
        </div>
      </div>

      {result && (
        <div className="space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-xs font-bold text-amber-700 mb-3">📝 풀이 과정</p>
            <div className="space-y-1.5">
              {result.steps.map((step, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className={`text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    i === result.steps.length - 1 ? 'bg-violet-500 text-white' : 'bg-amber-200 text-amber-700'
                  }`}>{i + 1}</span>
                  <span className={`text-sm font-mono ${
                    i === result.steps.length - 1 ? 'font-black text-violet-700 text-base' : 'text-amber-800'
                  }`}>{step}</span>
                </div>
              ))}
            </div>
          </div>

          <div className={`rounded-xl px-5 py-4 border-2 ${
            mode === 'findY' ? 'bg-violet-50 border-violet-300' : 'bg-indigo-50 border-indigo-300'
          }`}>
            <div className="flex items-baseline gap-2">
              <span className={`text-3xl font-black ${
                mode === 'findY' ? 'text-violet-700' : 'text-indigo-700'
              }`}>{result.outputVal}</span>
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <p className="text-xs font-bold text-slate-600 mb-2">🧑‍🎓 답 맞춰보기</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={userAnswer}
                onChange={e => { setUserAnswer(e.target.value); setChecked(null) }}
                onKeyDown={e => e.key === 'Enter' && checkAnswer()}
                placeholder="내 답 입력"
                className={`flex-1 text-sm border-2 rounded-lg px-3 py-2 focus:outline-none transition-all ${
                  checked === null ? 'border-slate-200' : checked ? 'border-emerald-400 bg-emerald-50' : 'border-red-400 bg-red-50'
                }`}
              />
              <button
                onClick={checkAnswer}
                disabled={!userAnswer}
                className="px-4 py-2 rounded-lg bg-slate-700 text-white text-sm font-medium disabled:bg-slate-200 disabled:text-slate-400 hover:bg-slate-800 transition-colors"
              >
                확인
              </button>
            </div>
            {checked !== null && (
              <div className={`mt-2 flex items-center gap-2 text-sm font-semibold ${checked ? 'text-emerald-600' : 'text-red-600'}`}>
                {checked
                  ? <><CheckCircle size={16} /> 정답이에요! 🎉</>
                  : <><XCircle size={16} /> 다시 확인해보세요!</>}
              </div>
            )}
          </div>
          <button onClick={reset} className="text-xs text-slate-400 hover:text-slate-600 transition-colors">
            ↺ 다시 입력하기
          </button>
        </div>
      )}
    </div>
  )
}

// ── 퀴즈 컴포넌트 ─────────────────────────────────────────────────────────────
function QuizSection({ quiz }: { quiz: typeof SCENARIOS[0]['quiz'] }) {
  const [revealed, setRevealed] = useState<Record<number, boolean>>({})
  const [showHint, setShowHint] = useState<Record<number, boolean>>({})

  return (
    <div className="space-y-3">
      {quiz.map((q, i) => (
        <div key={i} className="border border-slate-200 rounded-xl p-4 bg-white">
          <p className="text-sm font-semibold text-slate-800 mb-2">❓ {q.q}</p>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setShowHint(h => ({ ...h, [i]: !h[i] }))}
              className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors flex items-center gap-1"
            >
              <Lightbulb size={11} /> 힌트
            </button>
            <button
              onClick={() => setRevealed(r => ({ ...r, [i]: !r[i] }))}
              className="text-xs px-3 py-1.5 rounded-full bg-violet-100 text-violet-700 hover:bg-violet-200 transition-colors"
            >
              {revealed[i] ? '정답 숨기기' : '정답 보기'}
            </button>
          </div>
          {showHint[i] && (
            <div className="mt-2 bg-amber-50 rounded-lg px-3 py-2 text-xs text-amber-700">
              💡 힌트: <span className="font-mono">{q.hint}</span>
            </div>
          )}
          {revealed[i] && (
            <div className="mt-2 bg-violet-50 border border-violet-200 rounded-lg px-3 py-2">
              <span className="text-sm font-bold text-violet-700">✅ 정답: {q.a}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ── 챌린지 카드 컴포넌트 ──────────────────────────────────────────────────────
function ChallengeCard({ ch }: { ch: typeof CHALLENGES[0] }) {
  const [showHint, setShowHint] = useState(false)
  const [revealed, setRevealed] = useState(false)

  return (
    <div className="card border border-slate-200">
      <div className="flex items-start gap-3 mb-3">
        <span className="text-2xl">{ch.emoji}</span>
        <span className={`text-xs px-2 py-1 rounded-full font-medium ${ch.levelColor}`}>{ch.level}</span>
      </div>
      <p className="text-sm text-slate-800 font-medium leading-relaxed mb-3">{ch.question}</p>
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setShowHint(h => !h)}
          className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors flex items-center gap-1"
        >
          <Lightbulb size={11} /> 힌트
        </button>
        <button
          onClick={() => setRevealed(r => !r)}
          className="text-xs px-3 py-1.5 rounded-full bg-violet-100 text-violet-700 hover:bg-violet-200 transition-colors flex items-center gap-1"
        >
          <Trophy size={11} /> {revealed ? '정답 숨기기' : '정답 확인'}
        </button>
      </div>
      {showHint && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
          💡 {ch.hint}
        </div>
      )}
      {revealed && (
        <div className="mt-3 bg-violet-50 border border-violet-200 rounded-xl p-3 space-y-2">
          <p className="text-sm font-bold text-violet-700">✅ 정답: {ch.answer}</p>
          <p className="text-xs text-violet-600 leading-relaxed whitespace-pre-line">{ch.explanation}</p>
        </div>
      )}
    </div>
  )
}

// ── 메인 페이지 ───────────────────────────────────────────────────────────────
type Tab = 'scenarios' | 'careers' | 'challenges'

export default function A1RealWorldPage() {
  const [selected, setSelected] = useState(SCENARIOS[0])
  const [showTable, setShowTable] = useState(false)
  const [filterCat, setFilterCat] = useState<string>('전체')
  const [activeTab, setActiveTab] = useState<Tab>('scenarios')

  const categories = ['전체', ...Array.from(new Set(SCENARIOS.map(s => s.category)))]
  const filtered = filterCat === '전체' ? SCENARIOS : SCENARIOS.filter(s => s.category === filterCat)

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'scenarios',  label: '시나리오 탐색',   icon: <MapPin size={14} /> },
    { id: 'careers',    label: '직업과 문자식',    icon: <Briefcase size={14} /> },
    { id: 'challenges', label: '도전 문제',        icon: <Trophy size={14} /> },
  ]

  return (
    <div className="p-6 max-w-7xl mx-auto">

      {/* ── 헤더 ── */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
            <Globe size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">꿈꾸는 직업과 문자식</h1>
            <p className="text-sm text-slate-500">게임 개발자, 셰프, 디자이너, 과학자, 쇼핑몰 운영자, 건축가, 의사 — 모두 문자식을 씁니다!</p>
          </div>
        </div>

        <div className="mt-4 rounded-2xl bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-600 text-white p-5">
          <p className="font-bold text-lg mb-1">🌟 꿈의 직업에서 문자식을 만나다!</p>
          <p className="text-sm opacity-90 mb-3">수학의 문자식 = 코딩의 변수. 세상 모든 공식이 문자식으로 이루어져 있습니다!</p>
          <div className="grid grid-cols-4 md:grid-cols-7 gap-2">
            {[
              { icon: '💻', job: '개발자' },
              { icon: '👨‍🍳', job: '셰프' },
              { icon: '🎨', job: '디자이너' },
              { icon: '🔬', job: '과학자' },
              { icon: '🛒', job: '쇼핑몰' },
              { icon: '🏗️', job: '건축가' },
              { icon: '👨‍⚕️', job: '의사' },
            ].map(({ icon, job }) => (
              <div key={job} className="bg-white/20 rounded-xl px-2 py-2 text-center hover:bg-white/30 transition-colors cursor-pointer">
                <p className="text-2xl">{icon}</p>
                <p className="text-xs font-bold mt-1">{job}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── 탭 ── */}
      <div className="flex gap-1 mb-6 bg-slate-100 p-1 rounded-xl w-fit">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-white text-violet-700 shadow-sm'
                : 'text-slate-600 hover:text-slate-800'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── TAB 1: 시나리오 탐색 ── */}
      {activeTab === 'scenarios' && (
        <>
          {/* 카테고리 필터 */}
          <div className="flex gap-2 mb-4 flex-wrap">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setFilterCat(cat)}
                className={`text-xs px-4 py-2 rounded-full font-medium transition-colors ${
                  filterCat === cat
                    ? 'bg-violet-600 text-white'
                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* ── 좌측: 시나리오 목록 ── */}
            <div className="space-y-3">
              {filtered.map(scenario => (
                <button
                  key={scenario.id}
                  onClick={() => { setSelected(scenario); setShowTable(false) }}
                  className={`w-full text-left rounded-2xl border-2 p-4 transition-all ${
                    selected.id === scenario.id
                      ? `${scenario.border} ${scenario.bg} shadow-md`
                      : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{scenario.emoji}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-slate-800">{scenario.title}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[scenario.category]}`}>
                          {scenario.category}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{scenario.situation}</p>
                      <div className="mt-1.5 font-mono text-xs text-violet-600 font-semibold">{scenario.equationLabel}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* ── 우측: 시나리오 상세 ── */}
            <div className="lg:col-span-2 space-y-4">

              {/* 직업 헤더 카드 */}
              <div className={`rounded-2xl border-2 ${selected.border} ${selected.bg} p-5`}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-4xl">{selected.emoji}</span>
                  <div>
                    <h2 className="text-xl font-bold text-slate-800">{selected.title}</h2>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[selected.category]}`}>
                      {selected.category}
                    </span>
                  </div>
                </div>

                {/* 이야기 */}
                <div className="mb-4 bg-white/70 rounded-xl p-4 border border-slate-100">
                  <p className="text-xs font-bold text-slate-500 mb-2">📖 이야기로 만나는 문자식</p>
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{selected.story}</p>
                </div>

                {/* 수학적 상황 */}
                <p className="text-sm font-semibold text-slate-600 mb-1">📌 수학적 상황</p>
                <p className="text-sm text-slate-700 leading-relaxed mb-3">{selected.situation}</p>

                {/* 문자식 */}
                <div className="bg-white rounded-xl p-4 border border-slate-100">
                  <p className="text-xs font-semibold text-slate-500 mb-3">📐 문자식으로 나타내기</p>
                  <div className="mb-3">
                    <span className="text-2xl font-black text-violet-700 font-mono">{selected.equationLabel}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                      <p className="text-xs font-bold text-orange-700">계수 (= {selected.slopeVal})</p>
                      <p className="text-xs text-orange-600 mt-0.5">{selected.slopeDesc}</p>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                      <p className="text-xs font-bold text-blue-700">상수항 (= {selected.interceptVal})</p>
                      <p className="text-xs text-blue-600 mt-0.5">{selected.interceptDesc}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 문제 & 풀이 */}
              <div className="card">
                <p className="text-sm font-semibold text-slate-700 mb-2">💬 문제</p>
                <p className="text-base text-slate-800 font-medium">{selected.question}</p>
                <div className="mt-3 bg-violet-50 border border-violet-200 rounded-xl px-4 py-3">
                  <p className="text-sm font-bold text-violet-700 whitespace-pre-line">✅ 풀이: {selected.answer}</p>
                </div>
                <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                  <p className="text-xs text-blue-700">
                    <span className="font-semibold">왜 문자식인가?</span> {selected.whyMath}
                  </p>
                </div>
              </div>

              {/* 표로 보기 */}
              <div className="card">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <TrendingUp size={15} className="text-violet-500" /> 표로 보기
                  </p>
                  <button
                    onClick={() => setShowTable(!showTable)}
                    className="text-xs flex items-center gap-1 text-slate-500 hover:text-slate-700 bg-slate-100 px-3 py-1 rounded-full"
                  >
                    {showTable ? '표 접기' : '표 펼치기'}
                    {showTable ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  </button>
                </div>
                {showTable && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr>
                          <th className="border border-slate-200 px-3 py-2 bg-orange-50 text-orange-700 text-left">{selected.xLabel}</th>
                          <th className="border border-slate-200 px-3 py-2 bg-violet-50 text-violet-700 text-left">{selected.yLabel}</th>
                          <th className="border border-slate-200 px-3 py-2 bg-slate-50 text-slate-600 text-left">변화율 (Δy/Δx)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selected.tableData.map((row, i) => (
                          <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                            <td className="border border-slate-200 px-3 py-2 font-mono font-bold text-orange-600">{row.x}</td>
                            <td className="border border-slate-200 px-3 py-2 font-mono font-bold text-violet-700">{row.y}</td>
                            <td className="border border-slate-200 px-3 py-2 font-mono text-slate-600">
                              {i === 0 ? '—' : (
                                <span className="font-mono text-emerald-600">
                                  {((row.y - selected.tableData[i - 1].y) / (row.x - selected.tableData[i - 1].x)).toFixed(2)}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="text-xs text-slate-400 mt-2 text-center">
                      변화율이 항상 <span className="font-bold text-emerald-600">{selected.slopeVal}</span>으로 일정 → 문자식 확인!
                    </p>
                  </div>
                )}
                {!showTable && (
                  <div className="bg-slate-50 rounded-xl p-4 text-center text-slate-500 text-sm">
                    <p className="text-2xl mb-1">📋</p>
                    <p>표 펼치기를 눌러 값의 변화를 확인해보세요!</p>
                  </div>
                )}
              </div>

              {/* 계산기 */}
              <ScenarioCalculator scenario={selected} />

              {/* 연습 문제 */}
              <div className="card">
                <p className="text-sm font-semibold text-slate-700 mb-3">🧩 연습 문제</p>
                <QuizSection quiz={selected.quiz} />
              </div>

              {/* 관련 직업 */}
              <div className="card bg-slate-50 border border-slate-200">
                <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Briefcase size={14} className="text-violet-500" /> 이 상황에서 문자식을 쓰는 직업
                </p>
                <div className="flex flex-wrap gap-2">
                  {selected.jobs.map(job => (
                    <span key={job} className="text-xs bg-violet-100 text-violet-700 px-3 py-1.5 rounded-full font-medium flex items-center gap-1">
                      <Star size={10} /> {job}
                    </span>
                  ))}
                </div>
              </div>

              {/* 실제 적용 */}
              <div className="rounded-2xl bg-gradient-to-r from-slate-800 to-slate-700 text-white p-5">
                <p className="text-sm font-bold mb-1 flex items-center gap-2">
                  <TrendingUp size={15} /> 실제로 어디서 쓰이나요?
                </p>
                <p className="text-sm text-slate-300 leading-relaxed">{selected.insight}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {['문자식', '대입', '동류항', '계수', '상수항', '식의 값'].map(tag => (
                    <span key={tag} className="text-xs bg-white/10 px-2 py-1 rounded-full">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* 핵심 개념 정리 */}
          <div className="mt-8 card bg-gradient-to-br from-violet-50 to-purple-50 border-violet-200">
            <h3 className="font-bold text-slate-800 mb-4 text-base">📌 문자식 핵심 개념 정리</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { icon: '🔤', title: '문자식이란?', desc: '수 대신 문자를 사용한 식\n예: y = 10a + 15\n문자에 수를 대입하면 식의 값 계산\n코딩의 변수와 동일한 원리!', color: 'text-violet-600' },
                { icon: '🔢', title: '계수와 상수항', desc: '계수: 문자 앞의 숫자\n예: 10a에서 10이 계수\n상수항: 문자 없는 숫자\n예: 10a+15에서 15가 상수항', color: 'text-indigo-600' },
                { icon: '✏️', title: '동류항 정리', desc: '같은 문자끼리 더할 수 있음\n3a + 2a = 5a (동류항)\n3a + 2b는 더 이상 정리 불가\n동류항끼리만 계산 가능!', color: 'text-purple-600' },
              ].map(({ icon, title, desc, color }) => (
                <div key={title} className="bg-white rounded-xl p-4 border border-slate-100">
                  <p className="text-2xl mb-2">{icon}</p>
                  <p className={`font-bold text-sm ${color}`}>{title}</p>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed whitespace-pre-line">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ── TAB 2: 직업과 문자식 ── */}
      {activeTab === 'careers' && (
        <div>
          <div className="mb-6 rounded-2xl bg-gradient-to-r from-violet-600 to-purple-600 text-white p-6">
            <h2 className="text-xl font-bold mb-2">💼 문자식을 사용하는 직업들</h2>
            <p className="text-sm text-violet-100">
              y = ax + b는 IT·의료·건축·요리·패션 등 수많은 직업의 핵심 분석 도구입니다!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
            {CAREERS.map(career => (
              <div key={career.title} className="card border border-slate-200 hover:shadow-md transition-shadow">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${career.color} flex items-center justify-center text-2xl mb-3`}>
                  {career.icon}
                </div>
                <h3 className="font-bold text-slate-800 mb-1">{career.title}</h3>
                <p className="text-xs text-slate-500 mb-3 leading-relaxed">{career.example}</p>
                <div className="bg-slate-50 rounded-lg p-3 mb-3">
                  <p className="text-xs text-slate-400 mb-1">실제 사용 공식</p>
                  <code className="text-xs font-mono text-violet-700 font-bold">{career.formula}</code>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs bg-violet-100 text-violet-700 px-2 py-1 rounded-full font-medium">
                    💰 {career.salary}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="card border border-slate-200 bg-gradient-to-br from-slate-800 to-slate-900 text-white mb-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Zap size={20} className="text-yellow-400" /> AI 시대에도 문자식이 필요한 이유
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { title: '프로그래밍·알고리즘', desc: '코딩의 변수(variable)는 수학의 문자와 완전히 같습니다. 문자식을 이해하면 코딩이 쉬워집니다!', color: 'border-yellow-400' },
                { title: '데이터 분석·머신러닝', desc: '머신러닝 모델은 y = w1x1 + w2x2 + b 같은 문자식이 기본. 문자식 없이 AI 개발 불가!', color: 'border-rose-400' },
                { title: '과학·공학 설계', desc: 'F=ma, E=mc², V=IR 등 자연의 모든 법칙이 문자식입니다. 과학의 언어 = 문자식!', color: 'border-emerald-400' },
                { title: '비즈니스·재무 분석', desc: '수익=가격×수량-비용, 할인가=0.8×정가. 모든 비즈니스 계산이 문자식으로 이루어집니다.', color: 'border-sky-400' },
              ].map(item => (
                <div key={item.title} className={`border-l-4 ${item.color} pl-4 py-2`}>
                  <p className="font-semibold text-sm mb-1">{item.title}</p>
                  <p className="text-xs text-slate-300 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="card border border-violet-200 bg-violet-50">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <ChevronRight size={18} className="text-violet-600" /> 문자식에서 시작하는 학습 경로
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              {[
                { step: '중1', topic: '문자와 식', active: true },
                { step: '중2', topic: '일차방정식', active: false },
                { step: '중3', topic: '이차방정식', active: false },
                { step: '고1', topic: '다항식·인수분해', active: false },
                { step: '고2', topic: '방정식·부등식', active: false },
                { step: '대학', topic: '선형대수', active: false },
              ].map((item, i) => (
                <React.Fragment key={`${item.step}-${item.topic}`}>
                  <div className={`rounded-xl px-3 py-2 text-center ${item.active ? 'bg-violet-600 text-white' : 'bg-white border border-slate-200 text-slate-600'}`}>
                    <p className="text-xs font-bold">{item.step}</p>
                    <p className="text-xs mt-0.5">{item.topic}</p>
                  </div>
                  {i < 5 && <ChevronRight size={14} className="text-slate-400 flex-shrink-0" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── TAB 3: 도전 문제 ── */}
      {activeTab === 'challenges' && (
        <div>
          <div className="mb-6 rounded-2xl bg-gradient-to-r from-violet-500 to-purple-600 text-white p-6">
            <h2 className="text-xl font-bold mb-2">🏆 실생활 도전 문제</h2>
            <p className="text-sm text-violet-100">
              실제 상황에서 문자식을 직접 세우고 계산해보세요!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
            {CHALLENGES.map(ch => (
              <ChallengeCard key={ch.id} ch={ch} />
            ))}
          </div>

          <div className="card border border-slate-200">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Star size={16} className="text-violet-500" /> 문자식 핵심 공식 정리
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { step: '① 문자식 세우기', desc: '문자: 변하는 수량을 대표\n계수: 문자 앞 숫자\n상수항: 변하지 않는 수\n같은 문자끼리 → 동류항!', example: '5x + 1000 (음료수+봉투)', color: 'bg-violet-50 border-violet-200 text-violet-800' },
                { step: '② 식의 값 구하기', desc: '문자에 수를 대입\n곱셈 기호 복원 → 계산\n단위 확인 필수!\n순서: 대입 → 곱셈 → 덧셈', example: 'x=800 → 5×800+1000=5000', color: 'bg-indigo-50 border-indigo-200 text-indigo-800' },
                { step: '③ 동류항 정리', desc: '같은 문자끼리 덧셈 가능\n3a+2a = 5a\n3a+2b = 그대로 (정리 불가)\n계수끼리 더하고 문자 유지!', example: '4a-6 = 2(2a-3)으로 정리', color: 'bg-purple-50 border-purple-200 text-purple-800' },
              ].map(item => (
                <div key={item.step} className={`border rounded-xl p-4 ${item.color}`}>
                  <p className="font-bold text-sm mb-2">{item.step}</p>
                  <p className="text-xs leading-relaxed mb-2 whitespace-pre-line">{item.desc}</p>
                  <div className="bg-white/60 rounded-lg px-2 py-1">
                    <p className="text-xs font-mono font-bold">{item.example}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 bg-violet-50 border border-violet-200 rounded-xl p-4 text-center">
              <p className="text-sm font-bold text-violet-700">상황 파악 → 문자식 세우기 → 대입해서 계산!</p>
              <p className="text-xs text-violet-500 mt-1">문자식의 흐름을 이해하면 어떤 실생활 문제도 수학으로 풀 수 있습니다!</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
